import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { searchProducts } from '../../api'
import './SearchBar.css'

/**
 * Realtime Search Component
 * 
 * Features:
 * - Debounced search (300ms)
 * - Keyboard navigation
 * - Click outside to close
 * - Fast response rendering
 */
export default function SearchBar() {
    const [query, setQuery] = useState('')
    const [results, setResults] = useState([])
    const [isOpen, setIsOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(-1)

    const inputRef = useRef(null)
    const containerRef = useRef(null)
    const debounceRef = useRef(null)

    const navigate = useNavigate()

    // Debounced search
    const handleSearch = useCallback((searchQuery) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
        }

        if (!searchQuery || searchQuery.length < 2) {
            setResults([])
            setIsOpen(false)
            return
        }

        setIsLoading(true)

        debounceRef.current = setTimeout(async () => {
            try {
                const data = await searchProducts(searchQuery, 8)
                setResults(data.results || [])
                setIsOpen(true)
                setSelectedIndex(-1)
            } catch (error) {
                console.error('Search error:', error)
                setResults([])
            } finally {
                setIsLoading(false)
            }
        }, 300) // 300ms debounce
    }, [])

    // Handle input change
    const handleChange = (e) => {
        const value = e.target.value
        setQuery(value)
        handleSearch(value)
    }

    // Keyboard navigation
    const handleKeyDown = (e) => {
        if (!isOpen || results.length === 0) {
            if (e.key === 'Enter' && query.length >= 2) {
                navigate(`/products?search=${encodeURIComponent(query)}`)
                setIsOpen(false)
            }
            return
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setSelectedIndex(prev =>
                    prev < results.length - 1 ? prev + 1 : prev
                )
                break
            case 'ArrowUp':
                e.preventDefault()
                setSelectedIndex(prev => prev > 0 ? prev - 1 : -1)
                break
            case 'Enter':
                e.preventDefault()
                if (selectedIndex >= 0 && results[selectedIndex]) {
                    const product = results[selectedIndex]
                    const productLink = product.slug && product.slug !== '' ? product.slug : String(product.id)
                    navigate(`/products/${productLink}`)
                    setIsOpen(false)
                    setQuery('')
                } else {
                    navigate(`/products?search=${encodeURIComponent(query)}`)
                    setIsOpen(false)
                }
                break
            case 'Escape':
                setIsOpen(false)
                setSelectedIndex(-1)
                break
            default:
                break
        }
    }

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }
        }
    }, [])

    return (
        <div className="search-bar" ref={containerRef}>
            <div className="search-input-wrapper">
                <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                    ref={inputRef}
                    type="text"
                    className="search-input"
                    placeholder="Search products..."
                    value={query}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => query.length >= 2 && setIsOpen(true)}
                />
                {isLoading && <div className="search-spinner" />}
            </div>

            {isOpen && results.length > 0 && (
                <div className="search-results">
                    {results.map((product, index) => {
                        // Ensure we have a valid slug, fallback to id if needed
                        // Convert ID to string to handle both string and number types from API
                        const productLink = product.slug && product.slug !== '' ? product.slug : String(product.id)

                        return (
                        <Link
                            key={product.id}
                            to={`/products/${productLink}`}
                            className={`search-result-item ${index === selectedIndex ? 'selected' : ''}`}
                            onClick={() => {
                                setIsOpen(false)
                                setQuery('')
                            }}
                        >

                            {product.images?.[0] && (
                                <img
                                    src={product.images[0].thumbnail}
                                    alt={product.name}
                                    className="search-result-image"
                                />
                            )}
                            <div className="search-result-info">
                                <span className="search-result-name">{product.name}</span>
                                <span className="search-result-price">
                                    ${product.price}
                                    {product.on_sale && (
                                        <span className="search-result-original">
                                            ${product.regular_price}
                                        </span>
                                    )}
                                </span>
                            </div>
                        </Link>
                        )
                    })}
                    <Link
                        to={`/products?search=${encodeURIComponent(query)}`}
                        className="search-view-all"
                        onClick={() => setIsOpen(false)}
                    >
                        View all results for "{query}"
                    </Link>
                </div>
            )}

            {isOpen && query.length >= 2 && results.length === 0 && !isLoading && (
                <div className="search-results">
                    <div className="search-no-results">
                        No products found for "{query}"
                    </div>
                </div>
            )}
        </div>
    )
}
